import { SingleSelectDropdown } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetFolderTree } from "@/api/datasetFolder";
import { hatcheryDropdownModes } from "@/app/theme";
import { flattenFolderTree } from "./folderTree";

// 드롭다운에서 "미분류로 꺼내기 / 루트로 꺼내기" 를 고르는 센티널. 폴더 uuid 와 섞이지
// 않도록 uuid 형식이 아닌 값을 쓴다.
export const noFolderValue = "__none__";

export type FolderSelectFieldProps = {
  tree: DatasetFolderTree;
  label: string;
  // 고른 폴더 uuid. `noFolderValue` 면 폴더 없음(미분류 / 루트).
  value: string;
  onValueChange: (value: string) => void;
  // "폴더 없음" 항목의 문구. 데이터셋 이동은 `미분류로 꺼내기`, 폴더 이동은 `루트로 꺼내기`.
  noFolderLabel: string;
  // 선택지에서 뺄 폴더. 자기 자신·자기 자손이다(폴더를 그 아래로 넣을 수 없다).
  excludeUuids?: ReadonlySet<string>;
  // 고를 수 없는 폴더와 그 사유. 깊이 2 위반처럼 서버가 422 로 막을 배치를 미리 잠근다.
  disabledReason?: (uuid: string, depth: number) => string | null;
  disabled?: boolean;
};

/**
 * 폴더 하나를 고르는 드롭다운.
 *
 * **계층을 경로 라벨로 표현한다.** c6 드롭다운 옵션 타입에는 group·depth 필드가 없다. 그래서
 * optgroup 이나 들여쓰기를 표현할 수 없다. 루트는 `Picking`, 자식은 `Picking / 2026-Q1` 이다.
 * 이 표기는 목록의 `폴더` 컬럼과 같은 규칙이므로 두 표면이 같은 이름을 쓴다. 건수는 옵션의
 * `count` 슬롯에 넣는다.
 */
export function FolderSelectField({
  tree,
  label,
  value,
  onValueChange,
  noFolderLabel,
  excludeUuids,
  disabledReason,
  disabled = false,
}: FolderSelectFieldProps): ReactNode {
  const options = [
    { value: noFolderValue, label: noFolderLabel },
    ...flattenFolderTree(tree)
      .filter((folder) => excludeUuids?.has(folder.uuid) !== true)
      .map((folder) => {
        const reason = disabledReason?.(folder.uuid, folder.depth) ?? null;
        return {
          value: folder.uuid,
          label: folder.path,
          count: folder.datasetCount,
          disabled: reason !== null,
          ...(reason === null ? {} : { description: reason }),
        };
      }),
  ];

  return (
    <SingleSelectDropdown
      label={label}
      mode={hatcheryDropdownModes.default}
      options={options}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      searchable
      fullWidth
    />
  );
}
